import { useRef, useState } from 'react';
import { Button, Group, Stack, Text, Image, Paper, ActionIcon, Tooltip } from '@mantine/core';
import { IconUpload, IconX, IconCrop } from '@tabler/icons-react';
import { api, imageUrl } from '../api/client';
import CropImageModal from './CropImageModal';

type Entity = 'technicians' | 'equipment' | 'accessories' | 'consumables' | 'customers' | 'orders' | 'parts' | 'company';

const ACCEPT = 'image/jpeg,image/png,image/webp';
const ALLOWED = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_BYTES = 3 * 1024 * 1024;

export default function ImageUpload({
  entity,
  value,
  onChange,
  label = 'Image',
}: {
  entity: Entity;
  value: string | null;
  onChange: (path: string | null) => void;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  // When a file was just uploaded, we open the crop modal against the stored URL so the user can frame it.
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  async function upload(f: File) {
    if (!ALLOWED.has(f.type)) { setErr('Only JPEG, PNG, or WebP allowed.'); return; }
    if (f.size > MAX_BYTES) { setErr('Max size 3 MB.'); return; }
    setErr(null);
    setBusy(true);
    try {
      const res = await api.uploadImage(entity, f);
      onChange(res.path);
      // Offer to crop right after a fresh upload.
      setCropSrc(imageUrl(res.path) ?? null);
    } catch (ex: any) {
      setErr(ex.message ?? 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) await upload(f);
    e.target.value = '';
  }

  async function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) await upload(f);
  }

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>{label}</Text>
      <Paper
        withBorder
        p="sm"
        style={{
          background: dragActive ? 'var(--mantine-color-orange-light)' : '#fff',
          minHeight: 140,
          borderStyle: dragActive ? 'dashed' : 'solid',
          borderColor: dragActive ? 'var(--mantine-color-orange-6)' : undefined,
          transition: 'background 120ms, border-color 120ms',
        }}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
      >
        {value ? (
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Tooltip label="Click to crop / reframe" withArrow>
              <Image
                src={imageUrl(value)}
                h={120}
                w="auto"
                fit="contain"
                style={{ background: '#fff', cursor: 'pointer' }}
                onClick={() => setCropSrc(imageUrl(value) ?? null)}
              />
            </Tooltip>
            <Group gap={4}>
              <Tooltip label="Crop / reframe">
                <ActionIcon variant="subtle" onClick={() => setCropSrc(imageUrl(value) ?? null)}>
                  <IconCrop size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Remove image">
                <ActionIcon variant="subtle" color="red" onClick={() => onChange(null)}>
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        ) : (
          <Stack gap={4} align="center" justify="center" h={120}>
            <Text size="sm" c="dimmed">Drag &amp; drop an image here, or</Text>
            <Button
              leftSection={<IconUpload size={14} />}
              variant="light"
              size="xs"
              onClick={() => inputRef.current?.click()}
              loading={busy}
            >
              Upload image
            </Button>
            <Text size="xs" c="dimmed">White-background JPEG, PNG, or WebP · ≤ 3 MB</Text>
          </Stack>
        )}
        <input type="file" accept={ACCEPT} ref={inputRef} hidden onChange={onFile} />
      </Paper>
      {value && (
        <Group>
          <Button leftSection={<IconUpload size={14} />} variant="subtle" size="xs" onClick={() => inputRef.current?.click()} loading={busy}>
            Replace image
          </Button>
        </Group>
      )}
      {err && <Text size="xs" c="red">{err}</Text>}

      {cropSrc && (
        <CropImageModal
          src={cropSrc}
          entity={entity}
          onClose={() => setCropSrc(null)}
          onSaved={(newPath) => { onChange(newPath); setCropSrc(null); }}
        />
      )}
    </Stack>
  );
}
